import { useMemo } from 'react'
import { VehicleAxleGroup } from './VehicleAxles'
import type { VehicleSceneTheme } from './VehicleSceneTheme'
import { useGLTF } from '@react-three/drei'
import * as THREE from 'three'
import dafXfModelUrl from '../../../assets/vehicles/DAF XF 105_BlendSwap.glb?url'
import vanModelUrl from '../../../assets/vehicles/fourgonnette.glb?url'

const M = (mm: number) => mm / 1000
export const TRUCK_CAB_LENGTH = 2500
const VAN_CAB_REAR_Z = 0.54
const VAN_CAB_BODY_WIDTH = 1.62
export const VAN_FRONT_OVERHANG_TO_WIDTH = (2.579 - VAN_CAB_REAR_Z) / 1.977
const CAB_MODEL_PARTS = ['Fahrerhaus', 'Fenster', 'Spiegel']
const VAN_NON_CAB_PARTS = new Set(['Platform', 'Rama', 'Rear'])
const VAN_AXLE_PART_NAME = /(?:wheel|whell|tyre|tire|шина|колес|диск|achse|axle|rad)/i

const range = (x0: number, x1: number, y0: number, y1: number, z0: number, z1: number) => ({
  position: [M((x0 + x1) / 2), M((z0 + z1) / 2), M((y0 + y1) / 2)] as [number, number, number],
  args: [M(x1 - x0), M(z1 - z0), M(y1 - y0)] as [number, number, number],
})

export function VehicleCab({ width, verticalOffset = 0 }: { width: number; verticalOffset?: number }) {
  const { scene } = useGLTF(dafXfModelUrl)
  const { object: cabParts, bounds } = useMemo(() => {
    const object = new THREE.Group()
    for (const name of CAB_MODEL_PARTS) {
      const part = scene.getObjectByName(name)
      if (!part) throw new Error(`La pièce « ${name} » est introuvable dans le modèle DAF XF 105.`)
      object.add(part.clone(true))
    }

    object.updateMatrixWorld(true)
    const cab = object.getObjectByName('Fahrerhaus')
    if (!cab) throw new Error('La géométrie de cabine est absente du modèle DAF XF 105.')
    const bounds = new THREE.Box3().setFromObject(cab)
    object.traverse(part => {
      if (part instanceof THREE.Mesh) {
        part.castShadow = true
        part.receiveShadow = true
      }
    })
    return { object, bounds }
  }, [scene])

  const size = bounds.getSize(new THREE.Vector3())
  const widthScale = M(width) / size.x
  const lengthScale = M(TRUCK_CAB_LENGTH) / size.z
  const transformedMinX = bounds.min.z * lengthScale
  const transformedMinY = bounds.min.y * widthScale
  const transformedMinZ = -bounds.max.x * widthScale
  const transformedMaxZ = -bounds.min.x * widthScale

  return (
    <group
      position={[
        M(-TRUCK_CAB_LENGTH) - transformedMinX,
        -transformedMinY + M(verticalOffset),
        M(width / 2) - (transformedMinZ + transformedMaxZ) / 2,
      ]}
      rotation={[0, Math.PI / 2, 0]}
      scale={[widthScale, widthScale, lengthScale]}
    >
      <primitive object={cabParts} />
    </group>
  )
}

export function VanCab({ width }: { width: number }) {
  const { scene } = useGLTF(vanModelUrl)
  const { object, bounds } = useMemo(() => {
    const object = scene.clone(true)
    const clippingPlane = new THREE.Plane(new THREE.Vector3(-1, 0, 0), 0)

    object.traverse(part => {
      if (VAN_NON_CAB_PARTS.has(part.name) || VAN_AXLE_PART_NAME.test(part.name)) {
        part.visible = false
        part.traverse(child => {
          child.visible = false
        })
      }
      if (part instanceof THREE.Mesh && part.visible) {
        const clipMaterial = (material: THREE.Material) => {
          const clone = material.clone()
          clone.clippingPlanes = [clippingPlane]
          clone.clipShadows = true
          return clone
        }
        part.material = Array.isArray(part.material)
          ? part.material.map(clipMaterial)
          : clipMaterial(part.material)
        part.castShadow = true
        part.receiveShadow = true
      }
    })

    object.updateMatrixWorld(true)
    const bounds = new THREE.Box3()
    object.traverse(part => {
      if (!(part instanceof THREE.Mesh)) return
      let ancestor: THREE.Object3D | null = part
      while (ancestor && ancestor !== object.parent) {
        if (!ancestor.visible) return
        ancestor = ancestor.parent
      }
      part.geometry.computeBoundingBox()
      if (part.geometry.boundingBox) {
        bounds.union(part.geometry.boundingBox.clone().applyMatrix4(part.matrixWorld))
      }
    })
    return { object, bounds }
  }, [scene])

  const scale = M(width) / VAN_CAB_BODY_WIDTH

  return (
    <group
      position={[
        VAN_CAB_REAR_Z * scale,
        -bounds.min.y * scale,
        M(width / 2) - ((bounds.min.x + bounds.max.x) / 2) * scale,
      ]}
      rotation={[0, -Math.PI / 2, 0]}
      scale={scale}
    >
      <primitive object={object} />
    </group>
  )
}

export function VehicleTractor({
  length, width, fifthWheelX, theme, axles = [], trackWidth, axleYOffset = 0, axleVerticalOffsetY = 0,
}: {
  length: number; width: number; fifthWheelX: number; theme: VehicleSceneTheme
  axles?: any[]; trackWidth?: number; axleYOffset?: number; axleVerticalOffsetY?: number
}) {
  const railHeight = 850
  const leftRail = range(TRUCK_CAB_LENGTH, length, width * 0.15, width * 0.28, 300, railHeight)
  const rightRail = range(TRUCK_CAB_LENGTH, length, width * 0.72, width * 0.85, 300, railHeight)
  const deck = range(TRUCK_CAB_LENGTH, TRUCK_CAB_LENGTH + 1200, width * 0.28, width * 0.72, railHeight, railHeight + 60)

  return (
    <group>
      <group position={[M(TRUCK_CAB_LENGTH), 0, 0]}><VehicleCab width={width} /></group>
      {axles.map((axle, index) => (
        <group key={axle.id ?? index} position={[0, axleVerticalOffsetY, 0]}>
          <VehicleAxleGroup axle={{ ...axle, position: Number(axle.position ?? 0) + axleYOffset }} index={index} width={width} trackWidth={trackWidth} showLabels={false} theme={theme} />
        </group>
      ))}
      {[leftRail, rightRail, deck].map((part, index) => (
        <mesh key={index} position={part.position} castShadow receiveShadow>
          <boxGeometry args={part.args} />
          <meshStandardMaterial
            color="#94a3b8"
            emissive="#334155"
            emissiveIntensity={0.16}
            metalness={0.78}
            roughness={0.28}
          />
        </mesh>
      ))}
      <mesh position={[M(fifthWheelX), M(railHeight + 55), M(width / 2)]}><cylinderGeometry args={[M(380), M(380), M(40), 24]} /><meshStandardMaterial color="#9333ea" metalness={0.6} roughness={0.35} /></mesh>
    </group>
  )
}
